import { Quaternion, Vector3 } from '@babylonjs/core/Maths/math';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { Observable } from '@babylonjs/core/Misc/observable';
import { UtilityLayerRenderer } from '@babylonjs/core/Rendering/utilityLayerRenderer';
import { eViewpointTargetMode, eViewpointType } from '@models/classes/enums';
import { MeshNode } from '@models/classes/meshes';
import { ViewpointNode } from '@models/classes/viewpoints';
import { rotationToVector } from '@view/helpers/math-helper';
import { createViewpointVisualization } from '@view/helpers/viewpoint-helper';
import { KbViewer } from '@view/kb-viewer';
import { KbGizmo } from './gizmo';
import { KbPositionGizmo } from './position-gizmo';
import { KbRotationGizmo } from './rotation-gizmo';

/**
 * Gizmo that enables dragging and rotating lights
 */
export class KbViewpointGizmo extends KbGizmo {
    posGizmo: KbPositionGizmo | undefined;
    targetGizmo: KbPositionGizmo | undefined;
    rotGizmo: KbRotationGizmo | undefined;

    _attachedViewpoint: ViewpointNode | undefined;
    _gizmoMesh: AbstractMesh | undefined;
    _visualMesh: AbstractMesh | undefined;
    _targetVisualMesh: AbstractMesh | undefined;

    viewpointPositionGizmo: Mesh | undefined;

    /** Fires an event when any of it's sub gizmos are dragged */
    onDragStartObservable = new Observable<void>();
    /** Fires an event while any of it's sub gizmos are dragged */
    onDragObservable = new Observable<{ deltaPos?: Vector3; deltaTargetPos?: Vector3; deltaRotation?: Quaternion }>();
    /** Fires an event when any of it's sub gizmos are released from dragging */
    onDragEndObservable = new Observable<{
        deltaPos?: Vector3;
        deltaTargetPos?: Vector3;
        deltaRotation?: Quaternion;
    }>();

    previousTargetMode?: eViewpointTargetMode;

    attachTo(viewpoint: ViewpointNode) {
        // Get rid of any old gizmos
        if (this.posGizmo) {
            this.posGizmo.dispose();
            this.posGizmo = undefined;
        }
        if (this.targetGizmo) {
            this.targetGizmo.dispose();
            this.targetGizmo = undefined;
        }
        if (this.rotGizmo) {
            this.rotGizmo.dispose();
            this.rotGizmo = undefined;
        }

        // Create mesh to attach gizmos to, and the camera preview visualization attached to that mesh
        this._visualMesh = new AbstractMesh('', this.gizmoLayer.utilityLayerScene);
        const { targetPositionMarker, viewpointPositionGizmo } = createViewpointVisualization(
            viewpoint,
            this.gizmoLayer.utilityLayerScene
        );
        this._visualMesh.addChild(viewpointPositionGizmo);

        // Always create the position gizmo
        this.posGizmo = new KbPositionGizmo(this.kbViewer, this.gizmoLayer);
        this.previousTargetMode = viewpoint.targetMode;

        // Determine which gizmos to show. No extra gizmos for overhead camera (only position)
        // For the target point type viewpoint, make a separate mesh to hold the position gizmo
        if (viewpoint.type === eViewpointType.overhead) {
            viewpointPositionGizmo.scaling = new Vector3(1, -1, 1);
            viewpointPositionGizmo.bakeCurrentTransformIntoVertices();
        } else {
            if (viewpoint.targetMode === eViewpointTargetMode.rotation) {
                this.rotGizmo = new KbRotationGizmo(this.kbViewer, this.gizmoLayer, { x: true, y: true, z: false });

                // The first person camera is oriented pointing parallel to the +Z axis
                if (viewpoint.type == eViewpointType.firstPerson) {
                    viewpointPositionGizmo.rotate(Vector3.Right(), Math.PI / 2);
                    viewpointPositionGizmo.bakeCurrentTransformIntoVertices();
                }
            } else if (viewpoint.targetMode === eViewpointTargetMode.point) {
                this._gizmoMesh = new AbstractMesh('', this.gizmoLayer.utilityLayerScene);
                this._targetVisualMesh = new AbstractMesh('', this.gizmoLayer.utilityLayerScene);
                if (targetPositionMarker) {
                    this._targetVisualMesh?.addChild(targetPositionMarker);
                }
                this.targetGizmo = new KbPositionGizmo(this.kbViewer, this.gizmoLayer);
            }
        }
        if (!this._gizmoMesh) {
            this._gizmoMesh = this._visualMesh;
        } else {
            this._visualMesh.isPickable = false;
        }

        this.updateHelperMeshes(viewpoint);

        // Set up event handlers on the gizmos
        if (this.posGizmo) {
            this.posGizmo.attachedMesh = this._gizmoMesh;
            const positionChange = Vector3.Zero();
            this.posGizmo.onDragStartObservable.add(event => {
                positionChange.setAll(0);
                this.onDragStartObservable.notifyObservers();
            });
            this.posGizmo.onDragObservable.add(event => {
                if (this._gizmoMesh !== this._visualMesh) {
                    this._visualMesh?.position.addInPlace(event.delta);
                }

                positionChange.addInPlace(event.delta);
                this.onDragObservable.notifyObservers({ deltaPos: event.delta });
                this.updateHelperMeshDirectionToRotation(this._attachedViewpoint!);
            });
            this.posGizmo.onDragEndObservable.add(event => {
                this.onDragEndObservable.notifyObservers({ deltaPos: positionChange });
            });
        }
        if (this.targetGizmo) {
            this.targetGizmo.attachedMesh = this._targetVisualMesh!;
            const targetChange = Vector3.Zero();
            this.targetGizmo.onDragStartObservable.add(event => {
                targetChange.setAll(0);
                this.onDragStartObservable.notifyObservers();
            });
            this.targetGizmo.onDragObservable.add(event => {
                targetChange.addInPlace(event.delta);
                this.onDragObservable.notifyObservers({ deltaTargetPos: event.delta });
                this.updateHelperMeshDirectionToRotation(this._attachedViewpoint!);
            });
            this.targetGizmo.onDragEndObservable.add(event => {
                this.onDragEndObservable.notifyObservers({ deltaTargetPos: targetChange });
            });
        }
        if (this.rotGizmo) {
            this.rotGizmo.attachedMesh = this._visualMesh;
            let rotationChange = this._visualMesh.rotationQuaternion || Quaternion.Identity();
            this.rotGizmo.onDragStartObservable.add(event => {
                this.onDragStartObservable.notifyObservers();
                rotationChange = Quaternion.Identity();
            });
            this.rotGizmo.onDragObservable.add(event => {
                const { delta } = event;
                rotationChange.multiplyInPlace(delta);
                this.onDragObservable.notifyObservers({ deltaRotation: delta });
            });
            this.rotGizmo.onDragEndObservable.add(() => {
                this.onDragEndObservable.notifyObservers({ deltaRotation: rotationChange });
            });
        }

        this._attachedViewpoint = viewpoint;
    }

    updateGizmoPosition(viewpointNode?: ViewpointNode) {
        if (viewpointNode && viewpointNode !== this._attachedViewpoint) {
            this._attachedViewpoint = viewpointNode;
        }

        // If the target mode changed, rebuild the gizmo
        if (this._attachedViewpoint && this.previousTargetMode !== this._attachedViewpoint.targetMode) {
            const viewpoint = this._attachedViewpoint;
            this.dispose(true);
            this.attachTo(viewpoint);
        } else {
            if (this._attachedViewpoint) {
                this.updateHelperMeshes(this._attachedViewpoint);
            }
            super.updateGizmoPosition();
        }
    }

    private updateHelperMeshes(viewpoint: ViewpointNode) {
        if (!this._visualMesh || !this._gizmoMesh) {
            return;
        }
        this._visualMesh.position = viewpoint.position.toVec3();
        this._gizmoMesh.position = viewpoint.position.toVec3();

        if (this._targetVisualMesh) {
            this._targetVisualMesh.position = viewpoint.targetPoint.toVec3();
        }

        if (viewpoint.targetMode === eViewpointTargetMode.rotation) {
            const eulerRot = viewpoint.rotation.toVec3().scale(Math.PI / 180);
            this._visualMesh.rotationQuaternion = Quaternion.FromEulerVector(eulerRot);
        } else {
            this.updateHelperMeshDirectionToRotation(viewpoint);
        }
    }

    private updateHelperMeshDirectionToRotation(viewpoint: ViewpointNode, delta?: Vector3) {
        if (!this._visualMesh) {
            return;
        }
        if (!delta) {
            delta = Vector3.Zero();
        }
        const _visualMesh = this._visualMesh;

        if (viewpoint.targetMode !== eViewpointTargetMode.rotation) {
            let direction: Vector3;

            if (this._targetVisualMesh) {
                direction = this._targetVisualMesh.position.subtract(_visualMesh.position);
            } else {
                // mesh
                const targetMeshNode = viewpoint._manager!.getById(viewpoint.targetMeshId!) as MeshNode;
                if (targetMeshNode) {
                    direction = targetMeshNode.position.subtract(viewpoint.position).toVec3();
                } else {
                    direction = Vector3.Zero();
                }
            }
            const rotation = rotationToVector(direction);
            _visualMesh.rotationQuaternion = rotation;

            // This code is for fixing the rotation of the camera gizmo so that the x axis is always in the XZ plane (keeping the camera model level with the horizon.)
            // But, it is not working quite right.
            // _visualMesh.computeWorldMatrix(true);
            // let newXAxis = _visualMesh.getDirection(Vector3.Right());
            // const correction = rotationToVector(direction.cross(Vector3.Up()), newXAxis);
            // _visualMesh.rotationQuaternion = _visualMesh.rotationQuaternion.multiply(correction);
        }
    }

    /**
     * Creates a PositionGizmo
     * @param gizmoLayer The utility layer the gizmo will be added to
     */
    constructor(
        private kbViewer: KbViewer,
        gizmoLayer: UtilityLayerRenderer = UtilityLayerRenderer.DefaultUtilityLayer
    ) {
        super(gizmoLayer);

        this.attachedMesh = null;
    }

    /**
     * Disposes of the gizmo
     */
    dispose(recreate?: boolean) {
        if (this.posGizmo) {
            this.posGizmo.dispose();
        }
        if (this.rotGizmo) {
            this.rotGizmo.dispose();
        }
        if (this.targetGizmo) {
            this.targetGizmo.dispose();
        }
        if (this._visualMesh) {
            this._visualMesh.dispose();
        }
        if (this._targetVisualMesh) {
            this._targetVisualMesh.dispose();
        }
        if (this.viewpointPositionGizmo) {
            this.viewpointPositionGizmo.dispose();
        }
        if (!recreate) {
            this.onDragStartObservable.clear();
            this.onDragEndObservable.clear();
            delete this._attachedViewpoint;
        }
    }
}
